import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import { Box, Button, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
	ClearProductPublishJob,
	RetryProductPublishJob,
} from '../../../UserData/_actions/Store/StoreProductPublishJobActions';
import { useAppDispatch } from '../../../Common/_hooks/useAppDispatch';
import useStoreIntegrationName from '../../../Common/_hooks/Store/useStoreIntegrationName';
import { FetchStoreData } from '../../../UserData/_actions/Store/StoreActions';
import type ProductPublishJobStore from '../../../UserData/_stores/Store/ProductPublish/ProductPublishJobStore';
import ProductPublishJobThumbnail from './ProductPublishJobThumbnail';

interface Props {
	index: number
	job: ProductPublishJobStore
	store: string
}

export default function ProductPublishJobRow(props: Props) {
	const dispatch = useAppDispatch()
	const [t] = useTranslation('account-v2')
	const [tR] = useTranslation('routes')
	const [cleared, setCleared] = useState(false)
	const [submitting, setSubmitting] = useState(false)
	const storeIntegrationName = useStoreIntegrationName(props.job.get('store_slug') || props.store)
	const needsStoreReconnect = props.job.get('error_slug') === 'product_publish_error_store_missing_scope'
	const canRetry = props.job.get('can_retry')
	const reconnectUrl = `${tR('/account/stores/connect/{{store}}', { store: props.job.get('store_slug') || props.store })}?connect=1`

	const statusColor = useMemo((): 'default' | 'success' | 'error' | 'info' => {
		switch (props.job.get('status')) {
			case 'published':
				return 'success'
			case 'failed':
				return 'error'
			case 'processing':
				return 'info'
			default:
				return 'default'
		}
	}, [props.job])

	const statusLabel = useMemo(() => {
		switch (props.job.get('status')) {
			case 'published':
				return t('Published')
			case 'failed':
				return t('Failed')
			case 'processing':
				return t('Publishing')
			case 'cancelled':
				return t('Cancelled')
			default:
				return t('Pending')
		}
	}, [props.job, t])

	const errorMessage = useMemo(() => {
		if (!props.job.get('error_slug')) return null

		switch (props.job.get('error_slug')) {
			case 'product_publish_error_unsupported_store':
				return t('The {{store}} integration is not supported for product publishing.', { store: storeIntegrationName })
			case 'product_publish_error_store_not_found':
				return t('The {{store}} store could not be found.', { store: storeIntegrationName })
			case 'product_publish_error_store_owner_mismatch':
				return t('The {{store}} store does not belong to the user who queued this publish.', { store: storeIntegrationName })
			case 'product_publish_error_store_flagged':
				return t('The {{store}} store connection needs attention before this product can be published.', { store: storeIntegrationName })
			case 'product_publish_error_store_missing_scope':
				return t("We need you to reconnect your {{store}} store because we're missing some permissions. After reconnecting, retry publishing this product.", { store: storeIntegrationName })
			case 'product_publish_error_invalid_payload':
				return t('The queued product data was invalid.')
			case 'product_publish_error_invalid_product_design':
				return t('This product design is no longer available and cannot be published.')
			case 'product_publish_error_api':
				return t('{{store}} returned an error while publishing this product.', { store: storeIntegrationName })
			case 'product_publish_error_etsy_shop_frozen':
				return t('Etsy cannot create new listings for this shop because the shop is frozen or otherwise limited in Etsy. Resolve the shop issue in Etsy before retrying.')
			case 'product_publish_error_shopify_daily_variant_limit':
				return t("You've reached Shopify's daily variant creation limit. Please wait a day, then retry publishing this product.")
			case 'product_publish_error_unexpected':
				return t('{{store}} returned an unexpected error while publishing this product.', { store: storeIntegrationName })
			case 'product_publish_error_interrupted_after_external_call':
				return t('Publishing was interrupted after contacting the store. Please retry this product.')
			case 'product_publish_error_interrupted_before_external_call':
				return t('Publishing was interrupted before contacting the store. Please retry this product.')
			default:
				return t('Something went wrong while publishing this product.')
		}
	}, [storeIntegrationName, props.job, t])

	const refreshStoreData = useCallback(() => dispatch(FetchStoreData(props.store)), [dispatch, props.store])

	const onRetry = useCallback(() => {
		setSubmitting(true)
		dispatch(RetryProductPublishJob(props.store, props.job.get('id')).set({
			onSuccess: () => {
				setSubmitting(false)
				refreshStoreData()
			},
			onFailure: () => {
				setSubmitting(false)
				refreshStoreData()
			},
		}))
	}, [dispatch, props.job, props.store, refreshStoreData])

	const onClear = useCallback(() => {
		setSubmitting(true)
		dispatch(ClearProductPublishJob(props.store, props.job.get('id')).set({
			onSuccess: () => {
				setSubmitting(false)
				setCleared(true)
				refreshStoreData()
			},
			onFailure: () => {
				setSubmitting(false)
				refreshStoreData()
			},
		}))
	}, [dispatch, props.job, props.store, refreshStoreData])

	if (cleared) {
		return null
	}

	return <Box
		component="div"
		sx={{
			borderTop: props.index === 0 ? 0 : 1,
			borderColor: 'divider',
			bgcolor: props.job.get('status') === 'failed'
				? theme => alpha(theme.palette.error.main, 0.035)
				: 'transparent',
			px: 1.25,
			py: 1,
		}}
	>
		<Stack spacing={0.5}>
			<Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
				<ProductPublishJobThumbnail job={props.job} />
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					spacing={1}
					alignItems={{ xs: 'flex-start', sm: 'center' }}
					justifyContent="space-between"
					sx={{ minWidth: 0, flexGrow: 1 }}
				>
					<Typography
						variant="body2"
						noWrap
						sx={{
							fontWeight: 500,
							minWidth: 0,
							maxWidth: '100%',
						}}
					>{props.job.get('title')}</Typography>
					<Stack
						direction="row"
						spacing={1}
						alignItems="center"
						justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
						sx={{
							flexShrink: 0,
							minHeight: 30,
						}}
					>
						<Chip
							size="small"
							variant="outlined"
							color={statusColor}
							label={statusLabel}
							sx={{
								minWidth: 88,
								justifyContent: 'center',
							}}
						/>
						{props.job.get('status') !== 'pending' && props.job.get('status') !== 'processing' ? <Tooltip title={t('Clear')}>
							<IconButton
								aria-label={t('Clear')}
								color="default"
								size="small"
								disabled={submitting}
								onClick={onClear}
							>
								<ClearRoundedIcon fontSize="small" />
							</IconButton>
						</Tooltip> : null}
					</Stack>
				</Stack>
			</Stack>
			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				spacing={1}
				alignItems={{ xs: 'flex-start', sm: 'center' }}
				justifyContent="space-between"
			>
				{errorMessage ? <Typography
					variant="caption"
					color="error"
					sx={{
						display: 'block',
						pl: { xs: 0, sm: 7 },
					}}
				>
					{errorMessage}
				</Typography> : null}
				{needsStoreReconnect || canRetry ? <Stack
					direction="row"
					spacing={1}
					alignItems="center"
					sx={{
						flexShrink: 0,
						ml: { xs: 0, sm: 'auto' },
					}}
				>
					{needsStoreReconnect ? <Button
						color="secondary"
						size="small"
						variant="outlined"
						component={Link}
						to={reconnectUrl}
					>{t('Reconnect')}</Button> : null}
					{canRetry ? <Button
						color="secondary"
						size="small"
						variant="outlined"
						startIcon={<ReplayRoundedIcon />}
						disabled={submitting}
						onClick={onRetry}
					>{t('Retry')}</Button> : null}
				</Stack> : null}
			</Stack>
		</Stack>
	</Box>
}
